import { francAll } from "franc";
import { iso6393 } from "iso-639-3";
import { dictLangToIso6393Candidates } from "./erpLangIso6393";

const iso6393ByCode = new Map<string, (typeof iso6393)[number]>();
for (const lang of iso6393) {
  iso6393ByCode.set(lang.iso6393.toLowerCase(), lang);
}

/** 与 franc 同属中文族（含 cmn、zho 及 iso639-1=zh 的变体） */
const CHINESE_ISO6393 = new Set<string>();
for (const lang of iso6393) {
  if (lang.iso6391 === "zh") CHINESE_ISO6393.add(lang.iso6393);
}
CHINESE_ISO6393.add("cmn");
CHINESE_ISO6393.add("zho");

const JAPANESE_ISO6393 = new Set<string>();
for (const lang of iso6393) {
  if (lang.iso6391 === "ja") JAPANESE_ISO6393.add(lang.iso6393);
}

const KOREAN_ISO6393 = new Set<string>();
for (const lang of iso6393) {
  if (lang.iso6391 === "ko") KOREAN_ISO6393.add(lang.iso6393);
}

const ENGLISH_ISO6393 = new Set<string>();
for (const lang of iso6393) {
  if (lang.iso6391 === "en") ENGLISH_ISO6393.add(lang.iso6393);
}
ENGLISH_ISO6393.add("eng");

/** 常见 ISO 639-3 等价（接口与 franc 可能不一致） */
const EQUIV_CLASSES: string[][] = [
  ["fil", "tgl"],
  ["nob", "nno", "nor"],
];

function equivClassOf(code: string): Set<string> {
  const c = code.toLowerCase();
  for (const group of EQUIV_CLASSES) {
    if (group.includes(c)) return new Set(group);
  }
  return new Set([c]);
}

const RE_KANA = /[\u3040-\u309f\u30a0-\u30ff\u31f0-\u31ff]/g;
const RE_HANGUL = /[\uac00-\ud7af\u1100-\u11ff\u3130-\u318f]/g;
const RE_LATIN = /[A-Za-z]/g;
const RE_CJK = /[\u4e00-\u9fff]/g;
const RE_ARABIC = /[\u0600-\u06FF\u0750-\u077F]/g;
const RE_CYRILLIC = /[\u0400-\u04FF]/g;
const RE_THAI = /[\u0E00-\u0E7F]/g;
const RE_DEVANAGARI = /[\u0900-\u097F]/g;
const RE_GREEK = /[\u0370-\u03FF]/g;
const RE_HEBREW = /[\u0590-\u05FF]/g;

export type ScriptBucket =
  | "latin"
  | "cjk"
  | "hangul"
  | "kana"
  | "arabic"
  | "cyrillic"
  | "thai"
  | "devanagari"
  | "greek"
  | "hebrew";

function countMatches(s: string, re: RegExp): number {
  const m = s.match(re);
  return m ? m.length : 0;
}

function scriptCount(s: string, bucket: ScriptBucket): number {
  switch (bucket) {
    case "latin":
      return countMatches(s, RE_LATIN);
    case "cjk":
      return countMatches(s, RE_CJK);
    case "hangul":
      return countMatches(s, RE_HANGUL);
    case "kana":
      return countMatches(s, RE_KANA);
    case "arabic":
      return countMatches(s, RE_ARABIC);
    case "cyrillic":
      return countMatches(s, RE_CYRILLIC);
    case "thai":
      return countMatches(s, RE_THAI);
    case "devanagari":
      return countMatches(s, RE_DEVANAGARI);
    case "greek":
      return countMatches(s, RE_GREEK);
    case "hebrew":
      return countMatches(s, RE_HEBREW);
    default:
      return 0;
  }
}

function isScriptSignificant(count: number, total: number): boolean {
  if (count < 1 || total < 1) return false;
  if (count >= 3) return true;
  if (count >= 2 && total >= 3) return true;
  return count / total >= 0.14;
}

/**
 * 统计正文中「显著」的书写体系种类（≥2 种即视为多语混写，走宽松规则）。
 */
export function detectSignificantScripts(text: string): Set<ScriptBucket> {
  const s = text.trim();
  const buckets: ScriptBucket[] = [
    "latin",
    "cjk",
    "hangul",
    "kana",
    "arabic",
    "cyrillic",
    "thai",
    "devanagari",
    "greek",
    "hebrew",
  ];
  const counts = new Map<ScriptBucket, number>();
  let total = 0;
  for (const b of buckets) {
    const n = scriptCount(s, b);
    counts.set(b, n);
    total += n;
  }
  if (total === 0) return new Set();
  const out = new Set<ScriptBucket>();
  for (const b of buckets) {
    const n = counts.get(b) ?? 0;
    if (isScriptSignificant(n, total)) out.add(b);
  }
  return out;
}

function scriptSetLabelZh(scripts: Set<ScriptBucket>): string {
  const map: Record<ScriptBucket, string> = {
    latin: "拉丁字母",
    cjk: "汉字",
    hangul: "谚文",
    kana: "日文假名",
    arabic: "阿拉伯字母",
    cyrillic: "西里尔字母",
    thai: "泰文",
    devanagari: "天城文等",
    greek: "希腊字母",
    hebrew: "希伯来字母",
  };
  return [...scripts].map((x) => map[x] ?? x).join("、");
}

/** 单个 ISO 639-3 常见语种对应的书写体系（用于与正文脚本求交） */
function supportedScriptsForIso6393(iso3: string): Set<ScriptBucket> {
  const out = new Set<ScriptBucket>();
  const c = iso3.toLowerCase();

  if (CHINESE_ISO6393.has(c)) out.add("cjk");
  if (ENGLISH_ISO6393.has(c)) out.add("latin");
  if (JAPANESE_ISO6393.has(c)) {
    out.add("kana");
    out.add("cjk");
  }
  if (KOREAN_ISO6393.has(c)) out.add("hangul");

  const lang = iso6393ByCode.get(c);
  const i1 = lang?.iso6391?.toLowerCase();
  if (!i1) {
    if (out.size) return out;
    return new Set<ScriptBucket>(["latin"]);
  }

  if (out.size) return out;

  if (["ar", "fa", "ur", "ps", "sd"].includes(i1)) {
    out.add("arabic");
    return out;
  }
  if (["ru", "uk", "be", "bg", "mk", "kk", "ky", "mn", "tg", "tt", "ce", "ab", "os", "sr", "bs"].includes(i1)) {
    out.add("cyrillic");
    return out;
  }
  if (i1 === "el") {
    out.add("greek");
    return out;
  }
  if (["he", "yi", "iw"].includes(i1)) {
    out.add("hebrew");
    return out;
  }
  if (["th", "lo"].includes(i1)) {
    out.add("thai");
    return out;
  }
  if (["hi", "bn", "mr", "ne", "gu", "pa", "or", "as", "kn", "ml", "ta", "te", "si"].includes(i1)) {
    out.add("devanagari");
    return out;
  }
  if (i1 === "ja") {
    out.add("kana");
    out.add("cjk");
    return out;
  }
  if (i1 === "ko") {
    out.add("hangul");
    return out;
  }
  if (i1 === "zh") {
    out.add("cjk");
    return out;
  }
  if (i1 === "en") {
    out.add("latin");
    return out;
  }

  out.add("latin");
  return out;
}

function supportedScriptsForSelectedLang(selected: Set<string>): Set<ScriptBucket> {
  const out = new Set<ScriptBucket>();
  for (const code of selected) {
    for (const s of supportedScriptsForIso6393(code)) out.add(s);
  }
  return out;
}

/** franc 为 und 时用脚本粗分（短句/纯汉字等） */
function inferIso6393WhenUnd(text: string): string {
  const s = text;
  if (RE_KANA.test(s)) return "jpn";
  if (RE_HANGUL.test(s)) return "kor";
  const cjk = countMatches(s, RE_CJK);
  const lat = countMatches(s, RE_LATIN);
  if (lat > 0 && lat >= cjk) return "eng";
  if (cjk > 0) return "cmn";
  return "und";
}

function iso6393DisplayZh(code: string): string {
  const c = code.toLowerCase();
  if (c === "und") return "无法识别";
  if (typeof Intl !== "undefined") {
    try {
      const dn = new Intl.DisplayNames(["zh-Hans"], { type: "language" });
      const lab = dn.of(c);
      if (lab) return lab;
    } catch {
      // ignore
    }
  }
  return iso6393ByCode.get(c)?.name ?? code;
}

function selectedHits(set: Set<string>, bucket: Set<string>): boolean {
  for (const s of set) {
    if (bucket.has(s)) return true;
  }
  return false;
}

function isCompatible(detected: string, selected: Set<string>): boolean {
  const d = detected.toLowerCase();
  if (d === "und") return false;
  if (selected.has(d)) return true;

  const dClass = equivClassOf(d);
  for (const s of selected) {
    const sClass = equivClassOf(s);
    for (const x of dClass) {
      if (sClass.has(x)) return true;
    }
  }

  if (CHINESE_ISO6393.has(d) && selectedHits(selected, CHINESE_ISO6393)) return true;
  if (JAPANESE_ISO6393.has(d) && selectedHits(selected, JAPANESE_ISO6393)) return true;
  if (KOREAN_ISO6393.has(d) && selectedHits(selected, KOREAN_ISO6393)) return true;

  const det = iso6393ByCode.get(d);
  const d1 = det?.iso6391?.toLowerCase();
  if (d1) {
    for (const s of selected) {
      const s1 = iso6393ByCode.get(s.toLowerCase())?.iso6391?.toLowerCase();
      if (s1 && s1 === d1) return true;
    }
  }

  return false;
}

function pickDetectedCodes(trimmed: string): string[] {
  const minLen = Math.min(12, Math.max(3, Math.floor(trimmed.length / 2)));
  let top = francAll(trimmed, { minLength: minLen }).filter(([c]) => c !== "und");
  if (top.length === 0) {
    top = francAll(trimmed, { minLength: 1 }).filter(([c]) => c !== "und");
  }
  let primary = top[0]?.[0] ?? "und";
  if (primary === "und") {
    primary = inferIso6393WhenUnd(trimmed);
  }
  const rest = top.map(([c]) => c).filter((c) => c && c !== "und");
  return [primary, ...rest].filter((c, i, a) => a.indexOf(c) === i);
}

/**
 * 使用 `franc`（ISO 639-3）检测试听文本，与字典项解析出的标准码集合比对。
 * **多书写体系**（如拉丁+汉字、谚文+英文等）：只要所选语种覆盖其中**至少一种**显著脚本即通过。
 * 字典 `value` 无法映射到任何 ISO 639-3 时不拦截。
 */
export function validatePreviewTextForLang(
  text: string,
  langCode: string,
  langItems: Dict.DictItem[]
): { ok: true } | { ok: false; message: string } {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: false, message: "请填写试听文本。" };
  }

  const row = langItems.find((d) => String(d.value) === String(langCode));
  const selectedName = row?.name?.trim() || "当前所选语种";
  const selected = dictLangToIso6393Candidates(String(langCode ?? ""), String(row?.name ?? ""));

  if (selected.size === 0) {
    return { ok: true };
  }

  const scriptsInText = detectSignificantScripts(trimmed);
  if (scriptsInText.size >= 2) {
    const supported = supportedScriptsForSelectedLang(selected);
    for (const t of scriptsInText) {
      if (supported.has(t)) {
        return { ok: true };
      }
    }
    return {
      ok: false,
      message: `当前文本包含多种文字（${scriptSetLabelZh(scriptsInText)}），与所选语种「${selectedName}」不匹配，请切换语种或调整正文。`,
    };
  }

  const candidates = pickDetectedCodes(trimmed);
  const matched = candidates.some((c) => isCompatible(c, selected));

  if (matched) {
    return { ok: true };
  }

  const label = iso6393DisplayZh(candidates[0] ?? inferIso6393WhenUnd(trimmed));
  return {
    ok: false,
    message: `识别到您当前输入的文本主要为「${label}」，与所选语种「${selectedName}」不一致，请修改试听文本或切换语种。`,
  };
}

/**
 * 在字典列表中找第一个「可解析为 ISO 639-3」且与试听文本检测一致的 `value`。
 * **多书写体系**时不自动改下拉（避免误切）。
 */
export function findFirstResolvableLangValueMatchingText(
  text: string,
  langItems: Dict.DictItem[]
): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (detectSignificantScripts(trimmed).size >= 2) return null;
  for (const item of langItems) {
    const code = String(item.value ?? "");
    const name = String(item.name ?? "");
    if (dictLangToIso6393Candidates(code, name).size === 0) continue;
    if (validatePreviewTextForLang(trimmed, code, langItems).ok) return code;
  }
  return null;
}

/** @internal 单测 */
export function __testPickDetectedCodes(text: string): string[] {
  return pickDetectedCodes(text.trim());
}
